import {
  AddEventAsVisitorForm,
  createEventAsVisitorSchema,
  defaultCreateEventAsVisitorValues,
  useCreateEventAsVisitor,
} from "@ac-expo/api";
import {
  eventTypeTypeValues,
  getEventTypeTranslation,
} from "@ac-expo/api-types";
import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  FormCheckbox,
  FormDatePicker,
  FormInput,
  FormRadio,
  FormSelect,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TimePicker,
  useToast,
} from "@ac-expo/ui-components";
import { extractTime, toDatabaseFormat } from "@ac-expo/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Role } from "@prisma/client";
import { AllDatePickerTypes } from "@ui/components/ui/day-picker";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import environment from "@web-app/src/environments/environment";
import AuthStore from "@web-app/src/services/AuthStore";
import { addDays, differenceInCalendarDays, format } from "date-fns";
import { fr } from "date-fns/locale";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useState } from "react";
import { TimeValue } from "react-aria";
import { DateRange } from "react-day-picker";
import { useForm, useWatch } from "react-hook-form";
import addEventStore from "../../services/AddEventStore";
import PlacesList from "./PlacesList";

const timesValues = "dates.times";

type AddEventProps = {
  className?: string;
  TriggerElement?: FC<{ onClick: () => void }>;
};

const AddEvent = observer(({ className, TriggerElement }: AddEventProps) => {
  const form = useForm<AddEventAsVisitorForm>({
    resolver: zodResolver(createEventAsVisitorSchema),
    defaultValues: defaultCreateEventAsVisitorValues,
    mode: "all",
  });
  const { user } = useUserContext();
  const { control, trigger, getValues, reset, setValue } = form;
  const { mutate } = useCreateEventAsVisitor();
  const { toast } = useToast();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [doApplytoAll, setDoApplytoAll] = useState(false);

  useEffect(() => {
    if (addEventStore.getModalIsVisible()) {
      setDialogOpen(true);
      addEventStore.setModalVisible(false);
    }
  }, [addEventStore.getModalIsVisible()]);

  async function handleSubmit() {
    const isFormValid = await trigger();
    if (!isFormValid) {
      return;
    }
    const formValues = getValues();

    mutate(formValues, {
      onSuccess: (data) => {
        reset();
        setDialogOpen(false);
        toast({
          title: "Votre événement a été envoyé et est en cours de validation",
        });
      },
      onError: (error) => {
        toast({
          title: "Erreur lors de la création de l'événement",
          description: error.message,
        });
      },
    });
  }

  const times = useWatch({ control: form.control, name: timesValues });

  const onOpenChange = (open: boolean) => {
    if (user === undefined) {
      return;
    }
    setDialogOpen(open);
    reset();
  };

  const onTriggerClick = () => {
    if (user === undefined) {
      AuthStore.setModalVisible(true);
      return;
    }
    if (user.roles.some((r) => r.role === Role.organiser)) {
      const backofficeCreateEventUrl = new URL(
        `/organiser/events?create=true&connected_as=${user.id}`,
        environment.BACKOFFICE_URL
      );
      window.open(backofficeCreateEventUrl, "_blank");
      return;
    }
    return setDialogOpen(true);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      {TriggerElement !== undefined ? (
        <TriggerElement onClick={onTriggerClick} />
      ) : (
        <button onClick={onTriggerClick} className={cn("text-sm", className)}>
          Ajouter un événement
        </button>
      )}
      <DialogContent className="max-h-[calc(100vh-60px)] overflow-auto bg-white md:max-h-[calc(100vh-120px)] md:min-w-[741px]">
        <DialogHeader className="text-2xl">
          <DialogTitle className="flex justify-center text-left text-lg text-gray-900">
            Ajouter un événement
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="flex w-full flex-col gap-4">
              <div className="flex w-full flex-wrap gap-4">
                <FormInput
                  control={control}
                  name={"name"}
                  label={"Nom de l'événement"}
                  className="grow-[999]"
                  placeholder={"Nom"}
                  errorRing
                />

                <FormDatePicker
                  control={control}
                  className="grow"
                  mode="range"
                  name="dates"
                  label="Plage de dates"
                  onPick={(date: AllDatePickerTypes | undefined) => {
                    let range: DateRange | undefined = date as DateRange;
                    if (!range)
                      range = {
                        from: getValues().dates.from,
                        to: getValues().dates.from,
                      };
                    else if (!range.to) {
                      range = { from: range.from, to: range.from };
                    }

                    setValue("dates.from", range.from ?? new Date());
                    setValue("dates.to", range.to ?? new Date());

                    range.to = getValues().dates.to;
                    range.from = getValues().dates.from;
                    const dayCount =
                      differenceInCalendarDays(range.to, range.from) + 1;

                    const times = Array.from(
                      { length: dayCount },
                      (_, index) => {
                        const start = addDays(
                          new Date(range.from ?? new Date()),
                          index
                        );
                        start.setHours(8, 0, 0, 0);

                        const end = addDays(
                          new Date(range.from ?? new Date()),
                          index
                        );
                        end.setHours(18, 0, 0, 0);

                        return {
                          start: extractTime(start),
                          end: extractTime(end),
                          day: toDatabaseFormat(
                            addDays(new Date(range.from ?? new Date()), index)
                          ),
                        };
                      }
                    );
                    setValue(timesValues, times);
                    trigger("dates");
                  }}
                />
              </div>
              <PlacesList />
              <FormSelect
                control={control}
                items={eventTypeTypeValues.map((type) => ({
                  value: type,
                  label: getEventTypeTranslation(type),
                }))}
                name="type"
                label="Type d'événement"
                placeholder="Sélectionner"
                hasMessage={false}
                errorRing
              />

              <div className="flex w-full flex-1 flex-col gap-2 md:w-1/2">
                <div
                  className={cn(
                    "flex flex-1 flex-row rounded-xl bg-gray-50 p-4",
                    {
                      "ring-destructive focus-visible:ring-destructive focus-visible:ring-offset-background ring-1 focus-visible:ring-offset-1":
                        form.formState.errors.venue,
                    }
                  )}
                >
                  <FormCheckbox
                    control={form.control}
                    name="venue.indoor"
                    labelContent="Intérieur"
                    labelClassName="text-sm font-bold text-gray-500"
                    hasMessage={false}
                    onCheckedAfterChange={() => trigger("venue")}
                  />

                  <FormCheckbox
                    control={form.control}
                    name="venue.outdoor"
                    labelContent="Extérieur"
                    labelClassName="text-sm font-bold text-gray-500"
                    hasMessage={false}
                    onCheckedAfterChange={() => trigger("venue")}
                  />
                </div>
              </div>
              <h2 className="text-left text-lg font-bold text-gray-900">
                Éléments facultatifs
              </h2>
              <div className="grid w-full gap-4 md:grid-cols-2">
                <FormInput
                  control={form.control}
                  name={"organiserName"}
                  label={"Organisateur"}
                  placeholder={"Nom"}
                  className={"grow"}
                />
                <FormInput
                  control={form.control}
                  name={"organiserPhone"}
                  label={"Téléphone Organisateur"}
                  placeholder={"01 23 45 67 89"}
                  className={"grow"}
                  type={"tel"}
                />
              </div>
              <div className="grid w-full gap-4">
                <FormInput
                  control={form.control}
                  name={"organiserEmail"}
                  label={"Email organisateur"}
                  placeholder={"organisateur@mail.fr"}
                  className={"grow"}
                />
              </div>
              <FormInput
                control={form.control}
                name={"websiteUrl"}
                label={"Site Internet "}
                placeholder={"Site internet"}
              />

              {form.getValues().dates.from && form.getValues().dates.to && (
                <>
                  <p className="text-left text-lg text-gray-900">Horaires</p>
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="doApplytoAll"
                      checked={doApplytoAll}
                      onCheckedChange={() => setDoApplytoAll(!doApplytoAll)}
                    />
                    <label
                      htmlFor="doApplytoAll"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Appliquer à toutes les dates
                    </label>
                  </div>
                  <div className="grid max-h-[200px] w-full gap-4 overflow-auto rounded-lg bg-gray-50 p-4 min-[350px]:grid-cols-2 sm:grid-cols-3">
                    {form.getValues().dates.times.map((time, index) => {
                      if (doApplytoAll && index !== 0) {
                        return null;
                      }
                      return (
                        <div key={index} className={"w-full grid-cols-3"}>
                          <p className="text-xs">
                            {format(time.day, "dd MMMM", { locale: fr })}
                          </p>
                          <Popover>
                            <PopoverTrigger
                              className={"flex w-full grow"}
                              asChild
                            >
                              <Button
                                variant="outline"
                                className="flex grow bg-white text-sm font-light"
                              >
                                {times[index]?.start} - {times[index]?.end}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                              <div className="flex w-full gap-4">
                                <TimePicker
                                  value={
                                    {
                                      hour: parseInt(
                                        times[index]?.start.split(":")[0]
                                      ),
                                      minute: parseInt(
                                        times[index]?.start.split(":")[1]
                                      ),
                                      second: 0,
                                    } as TimeValue
                                  }
                                  onChange={(newTime) => {
                                    if (!newTime) return;
                                    const minutes =
                                      `${newTime.minute}`.padStart(2, "0");
                                    form.setValue(
                                      `dates.times.${index}.start`,
                                      `${newTime.hour}:${minutes}`
                                    );
                                  }}
                                  className="border-input bg-white font-normal"
                                />
                                <TimePicker
                                  value={
                                    {
                                      hour: parseInt(
                                        times[index]?.end.split(":")[0]
                                      ),
                                      minute: parseInt(
                                        times[index]?.end.split(":")[1]
                                      ),
                                      second: 0,
                                    } as TimeValue
                                  }
                                  onChange={(newTime) => {
                                    if (!newTime) return;
                                    const minutes =
                                      `${newTime.minute}`.padStart(2, "0");
                                    form.setValue(
                                      `dates.times.${index}.end`,
                                      `${newTime.hour}:${minutes}`
                                    );
                                  }}
                                  className="border-input bg-white font-normal"
                                />
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              <div className="flex flex-col md:w-1/2">
                <h4 className="mb-2 text-sm font-bold text-black">Entrée</h4>

                <div className="flex flex-wrap gap-4">
                  <FormRadio
                    control={form.control}
                    name="entry"
                    containerClassName="flex flex-1 flex-row rounded-xl bg-gray-50 p-4"
                    labelClassName="text-sm font-bold text-gray-500"
                    radioItems={[
                      { label: "Payante", value: "paid" },
                      { label: "Gratuite", value: "free" },
                    ]}
                  />
                </div>
              </div>
              <Button
                variant="blue"
                type="submit"
                className="mx-auto flex w-min justify-center font-semibold"
              >
                Envoyer
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});

export default AddEvent;
